import React, { useEffect, useMemo, useState } from 'react';
import { uploadFile } from './files';
import { supabase } from './supabase';
import { Plus, Trash2, Pencil, X, FileDown, ClipboardList, Store, PackageOpen, AlertCircle, ShoppingCart, Copy, CheckCircle2, Eraser } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Thumb } from './ui/Thumb';
import { ImageDrop } from './ui/ImageDrop';
import { PickerSelect } from './ui/PickerSelect';
import { loadPdfImages, drawImageInCell } from './pdfImages';
import { DataGrid, GridCol } from './ui/DataGrid';
import { TextCell, NumberCell, SelectCell, SaveState } from './ui/EditableCell';
import { useRowAutosave } from './ui/useRowAutosave';
import { cn } from './ui/Table';
// Componentes de uiarc.dev (ver src/ui/arc)
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { Input } from './ui/arc/input';
import { MetricCard } from './ui/arc/metric-card';
import { EmptyState } from './ui/arc/empty-state';
import { ChipGroup } from './ui/arc/chip-group';
import SegmentedControl from './ui/arc/segmented-control';
import { SearchField } from './ui/arc/search-field';

// Módulo Insumos — gestión de insumos como PADRE (grupo) + VARIANTES.
// - supply_groups: insumo padre (nombre, proveedor, imagen, unidad).
// - cost_materials: cada variante (con group_id, precio, moneda). Compatible con el costeo existente.
//
// UI: toda la pantalla vive dentro de `.tw` (tokens + preflight acotado de uiarc,
// ver src/ui/arc/foundation.css) y se arma con componentes de src/ui y src/ui/arc.

interface Provider { id: number; name: string; whatsapp: string | null; email: string | null; order_lang?: 'es' | 'en' | null; }
interface Group { id: string; name: string; name_en?: string | null; provider_id: number | null; image_url: string | null; unit: string | null; notes: string | null; }
interface Material { id: number; name: string; name_en?: string | null; type: string | null; unit_cost: string | number | null; currency: string | null; provider_id: number | null; group_id: string | null; image_url: string | null; }

type Flag = 'sin_precio' | 'sin_foto' | 'sin_proveedor' | 'usd';

export function Insumos() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  // Aviso de éxito (ej. "Pedido #X guardado"): sin esto el formulario se vaciaba en silencio.
  const [okMsg, setOkMsg] = useState<string | null>(null);
  // Buscador y "solo los del pedido" dentro del armado (proveedores con muchos insumos).
  const [ordSearch, setOrdSearch] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const [view, setView] = useState<'insumos' | 'pedidos'>('insumos');
  const [imgBusy, setImgBusy] = useState<number | null>(null);

  // ---- Insumo padre (panel de alta/edición) ----
  const [groupForm, setGroupForm] = useState<Partial<Group> | null>(null);
  const [groupImg, setGroupImg] = useState<File | null>(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [newProvOpen, setNewProvOpen] = useState(false);
  const [newProvName, setNewProvName] = useState('');
  // Preview local de la foto elegida antes de guardar (se libera al cambiarla).
  const groupImgUrl = useMemo(() => (groupImg ? URL.createObjectURL(groupImg) : null), [groupImg]);
  useEffect(() => () => { if (groupImgUrl) URL.revokeObjectURL(groupImgUrl); }, [groupImgUrl]);

  // ---- Pedidos ----
  const [orders, setOrders] = useState<any[]>([]);
  const [ordProvider, setOrdProvider] = useState<number | null>(null);
  const [qtys, setQtys] = useState<Record<number, number>>({});
  const [ordNotes, setOrdNotes] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);
  // Pedido del historial que se está EDITANDO (null = pedido nuevo).
  const [editingOrder, setEditingOrder] = useState<any | null>(null);
  // Alta rápida de insumo desde el armado del pedido.
  const [quickNew, setQuickNew] = useState<{ name: string; name_en: string; file: File | null } | null>(null);
  const [creatingQuick, setCreatingQuick] = useState(false);
  const quickImgUrl = useMemo(() => (quickNew?.file ? URL.createObjectURL(quickNew.file) : null), [quickNew?.file]);
  useEffect(() => () => { if (quickImgUrl) URL.revokeObjectURL(quickImgUrl); }, [quickImgUrl]);
  // Insumo recién duplicado: su nombre abre editable con el foco puesto.
  const [renameId, setRenameId] = useState<number | null>(null);

  // ---- Filtros del listado ----
  const [fProv, setFProv] = useState<number | null>(null);
  const [fFlag, setFFlag] = useState<Flag | null>(null);

  async function load() {
    setLoading(true);
    const [pr, gr, mt, ord] = await Promise.all([
      supabase.from('providers').select('id, name, whatsapp, email, order_lang').order('name'),
      supabase.from('supply_groups').select('*').eq('active', true).order('name'),
      supabase.from('cost_materials').select('id, name, name_en, type, unit_cost, currency, provider_id, group_id, image_url').order('name'),
      supabase.from('supply_orders').select('*, supply_order_items(*)').order('created_at', { ascending: false }).limit(50),
    ]);
    const err = pr.error || gr.error || mt.error;
    if (err) setMsg('No se pudieron cargar los insumos: ' + err.message);
    setProviders((pr.data || []) as Provider[]);
    setGroups((gr.data || []) as Group[]);
    setMaterials((mt.data || []) as Material[]);
    setOrders(ord.data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  // ── Borrador del pedido ──
  // Si salís a otra pestaña del admin con un pedido a medio armar, el componente se
  // desmonta y se perdía todo. Se guarda en este navegador y se recupera al volver.
  // (localStorage: es una comodidad de este usuario, no un dato compartido.)
  const DRAFT_KEY = 'krilic.insumos.pedido-borrador';
  const [draftRestored, setDraftRestored] = useState(false);
  useEffect(() => {
    if (loading || draftRestored) return;
    setDraftRestored(true);
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      if (!d || !d.provider || !d.qtys || !Object.keys(d.qtys).length) return;
      if (d.editingId) {
        const o = orders.find(x => x.id === d.editingId);
        if (!o) return;
        setEditingOrder(o);
      }
      // solo insumos que siguen existiendo
      const q: Record<number, number> = {};
      for (const [k, v] of Object.entries(d.qtys)) if (materials.some(m => m.id === Number(k))) q[Number(k)] = Number(v);
      if (!Object.keys(q).length) return;
      setOrdProvider(d.provider); setQtys(q); setOrdNotes(d.notes || ''); setView('pedidos');
      setOkMsg(`Recuperamos el pedido que estabas armando (${Object.keys(q).length} ítem(s)).`);
    } catch { /* sin storage: no pasa nada */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  useEffect(() => {
    if (!draftRestored) return;
    try {
      if (ordProvider && Object.keys(qtys).length) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ provider: ordProvider, qtys, notes: ordNotes, editingId: editingOrder?.id || null }));
      } else localStorage.removeItem(DRAFT_KEY);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordProvider, qtys, ordNotes, editingOrder, draftRestored]);

  const groupById = useMemo(() => new Map(groups.map(g => [g.id, g])), [groups]);
  const providerName = (id: number | null | undefined) => providers.find(p => p.id === id)?.name || null;

  // Imagen de una variante: la propia, y si no tiene, la del grupo padre.
  // Así el pedido siempre tiene algo visual, incluso con variantes recién creadas.
  const imgOf = (m: Material) => m.image_url || (m.group_id ? groupById.get(m.group_id)?.image_url : null) || null;
  // El proveedor de una variante: el del grupo padre, o el propio si no está agrupada.
  const provOf = (m: Material) => (m.group_id ? (groupById.get(m.group_id)?.provider_id ?? null) : m.provider_id);

  const isFlag = (m: Material, f: Flag) =>
    f === 'sin_precio' ? !(Number(m.unit_cost) > 0)
      : f === 'sin_foto' ? !imgOf(m)
        : f === 'sin_proveedor' ? provOf(m) == null
          : (m.currency || 'ARS') === 'USD';

  // Contadores del resumen y de los chips de filtro.
  const counts = useMemo(() => {
    const c: Record<Flag, number> = { sin_precio: 0, sin_foto: 0, sin_proveedor: 0, usd: 0 };
    for (const m of materials) (Object.keys(c) as Flag[]).forEach(f => { if (isFlag(m, f)) c[f]++; });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, groups]);

  // Filas de la grilla: una por VARIANTE, con su insumo padre resuelto. El buscador
  // del DataGrid filtra sobre las columnas que declaran `sort`; acá solo proveedor y chips.
  type GridRow = { id: number; m: Material; g: Group | undefined };
  const gridRows: GridRow[] = useMemo(
    () => materials
      .filter(m => (fFlag ? isFlag(m, fFlag) : true) && (fProv == null || provOf(m) === fProv))
      .map(m => ({ id: m.id, m, g: m.group_id ? groupById.get(m.group_id) : undefined })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [materials, groupById, fProv, fFlag],
  );

  // Autoguardado por fila: UI optimista + escritura diferida que acumula los cambios.
  const { edit, editNow, state } = useRowAutosave<Material>('cost_materials', setMaterials, {
    onError: (m) => setMsg('No se pudo guardar: ' + m),
  });

  // Cuántos insumos tiene cada proveedor (para los selectores).
  const countForProvider = (pid: number) => materials.filter(m => provOf(m) === pid).length;

  // ── Fotos ──────────────────────────────────────────────────────────────────
  // Sube la foto de UNA variante al bucket público supply-images y la guarda en el insumo.
  async function uploadVariantImage(m: Material, file: File) {
    setImgBusy(m.id);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `materials/${m.id}-${Date.now()}.${ext}`;
      const url = await uploadFile('supply-images', path, file); // R2 (02/11/2026)
      await editNow(m.id, { image_url: url });
      setMsg(null);
    } catch (e: any) {
      setMsg('No se pudo subir la imagen: ' + (e?.message || e));
    } finally {
      setImgBusy(null);
    }
  }

  // ── Insumo padre ───────────────────────────────────────────────────────────
  function newGroup() { setGroupForm({ name: '', name_en: '', provider_id: null, unit: '', notes: '' }); setGroupImg(null); setMsg(null); }
  function editGroup(g: Group) { setGroupForm({ ...g }); setGroupImg(null); setMsg(null); }
  function closeGroup() { setGroupForm(null); setGroupImg(null); setNewProvOpen(false); setNewProvName(''); }

  // Alta de proveedor inline desde el panel del insumo (sin salir a Proveedores)
  const addProviderInline = async () => {
    const name = newProvName.trim();
    if (!name) return;
    const { data, error } = await supabase.from('providers').insert({ name }).select().single();
    if (error || !data) { setMsg('No se pudo crear el proveedor'); return; }
    setProviders(ps => [...ps, data as Provider].sort((a, b) => a.name.localeCompare(b.name)));
    setGroupForm(f => f ? { ...f, provider_id: (data as Provider).id } : f);
    setNewProvOpen(false);
    setNewProvName('');
  };

  async function saveGroup() {
    if (!groupForm?.name?.trim()) { setMsg('Poné un nombre'); return; }
    setSavingGroup(true);
    try {
      let image_url = groupForm.image_url || null;
      if (groupImg) {
        const path = `${Date.now()}_${groupImg.name.replace(/[^\w.-]/g, '_')}`;
        image_url = await uploadFile('supply-images', path, groupImg); // R2 (02/11/2026)
      }
      const payload = { name: groupForm.name.trim(), name_en: groupForm.name_en?.trim() || null, provider_id: groupForm.provider_id || null, unit: groupForm.unit?.trim() || null, notes: groupForm.notes?.trim() || null, image_url, updated_at: new Date().toISOString() };
      if (groupForm.id) {
        const { error } = await supabase.from('supply_groups').update(payload).eq('id', groupForm.id);
        if (error) throw error;
      } else {
        // La grilla muestra VARIANTES: un insumo sin ninguna no aparecería. Se crea con
        // una primera variante con su mismo nombre, que después se renombra en la grilla.
        const { data, error } = await supabase.from('supply_groups').insert(payload).select().single();
        if (error) throw error;
        await supabase.from('cost_materials').insert({ name: payload.name, group_id: data.id, provider_id: payload.provider_id, unit_cost: 0, currency: 'ARS', type: 'simple' });
      }
      closeGroup(); setMsg(null); load();
    } catch (e: any) { setMsg('Error: ' + (e.message || e)); }
    setSavingGroup(false);
  }

  async function deleteGroup(g: Partial<Group>) {
    if (!g.id || !window.confirm(`¿Borrar el insumo "${g.name}"? Sus variantes quedan sin agrupar (no se borran).`)) return;
    await supabase.from('cost_materials').update({ group_id: null }).eq('group_id', g.id);
    await supabase.from('supply_groups').update({ active: false }).eq('id', g.id);
    closeGroup(); load();
  }

  async function addVariant(gid: string) {
    const g = groupById.get(gid);
    const { error } = await supabase.from('cost_materials').insert({ name: 'Nueva variante', group_id: gid, provider_id: g?.provider_id || null, unit_cost: 0, currency: 'ARS', type: 'simple' });
    if (error) { setMsg('No se pudo agregar la variante: ' + error.message); return; }
    load();
  }

  // ── Variantes ──────────────────────────────────────────────────────────────
  async function deleteVariant(m: Material) {
    if (!window.confirm(`¿Borrar la variante "${m.name}"? No se puede deshacer.`)) return;
    const { error } = await supabase.from('cost_materials').delete().eq('id', m.id);
    if (error) { setMsg('No se pudo borrar (puede estar usada en el costeo de un producto).'); return; }
    setMaterials(ms => ms.filter(x => x.id !== m.id));
  }
  // Duplicar = misma foto, precio, moneda, costeo, proveedor y grupo; solo cambia el nombre.
  // La copia queda AL LADO del original con el nombre seleccionado, para tipear el nuevo.
  async function duplicateMaterial(m: Material) {
    const { data, error } = await supabase.from('cost_materials').insert({
      name: `${m.name} (copia)`, name_en: m.name_en ? `${m.name_en} (copy)` : null,
      type: m.type || 'simple', unit_cost: m.unit_cost ?? 0, currency: m.currency || 'ARS',
      provider_id: m.provider_id, group_id: m.group_id, image_url: m.image_url,
    }).select('id, name, name_en, type, unit_cost, currency, provider_id, group_id, image_url').single();
    if (error || !data) { setMsg('No se pudo duplicar: ' + (error?.message || '')); return; }
    setMaterials(ms => {
      const i = ms.findIndex(x => x.id === m.id);
      const next = [...ms]; next.splice(i + 1, 0, data as Material); return next;
    });
    setRenameId((data as Material).id);
  }

  // Alta rápida desde el pedido: queda asignado al proveedor del pedido y con cantidad 1.
  async function createQuickMaterial() {
    if (!quickNew?.name.trim() || ordProvider == null) return;
    setCreatingQuick(true);
    const { data, error } = await supabase.from('cost_materials').insert({
      name: quickNew.name.trim(), name_en: quickNew.name_en.trim() || null,
      provider_id: ordProvider, unit_cost: 0, currency: 'ARS', type: 'simple',
    }).select('id, name, name_en, type, unit_cost, currency, provider_id, group_id, image_url').single();
    if (error || !data) { setMsg('No se pudo crear el insumo: ' + (error?.message || '')); setCreatingQuick(false); return; }
    setMaterials(ms => [...ms, data as Material]);
    setQtys(x => ({ ...x, [(data as Material).id]: 1 }));
    if (quickNew.file) await uploadVariantImage(data as Material, quickNew.file);
    setQuickNew(null); setCreatingQuick(false);
  }

  async function assignToGroup(materialId: number, gid: string) {
    await editNow(materialId, { group_id: gid || null });
  }

  // ── Pedido a proveedor + PDF ───────────────────────────────────────────────
  const providerItems = useMemo(() => {
    if (!ordProvider) return [] as { parent: string; key: string; items: Material[] }[];
    const byParent = new Map<string, { parent: string; key: string; items: Material[] }>();
    for (const m of materials) {
      if (provOf(m) !== ordProvider) continue;
      const key = m.group_id || '__ung';
      const parent = m.group_id ? (groupById.get(m.group_id)?.name || '—') : 'Sin agrupar';
      const e = byParent.get(key) || { parent, key, items: [] };
      e.items.push(m); byParent.set(key, e);
    }
    return Array.from(byParent.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordProvider, groupById, materials]);

  // Idioma del pedido: cada proveedor tiene el suyo (providers.order_lang). En inglés se usa
  // name_en y, si un insumo todavía no lo tiene, cae al nombre en español.
  const langOf = (pid: number | null | undefined): 'es' | 'en' => (providers.find(p => p.id === pid)?.order_lang === 'en' ? 'en' : 'es');
  const nameIn = (m: Material, lang: 'es' | 'en') => (lang === 'en' && m.name_en?.trim()) || m.name;
  const groupNameIn = (gid: string | null, lang: 'es' | 'en') => {
    const g = gid ? groupById.get(gid) : undefined;
    return g ? ((lang === 'en' && g.name_en?.trim()) || g.name) : '';
  };
  async function setProviderLang(pid: number, lang: 'es' | 'en') {
    setProviders(ps => ps.map(p => (p.id === pid ? { ...p, order_lang: lang } : p)));
    const { error } = await supabase.from('providers').update({ order_lang: lang }).eq('id', pid);
    if (error) setMsg('No se pudo guardar el idioma: ' + error.message);
  }

  function buildLines() {
    const lang = langOf(ordProvider);
    const lines: any[] = [];
    for (const m of materials) {
      const q = Number(qtys[m.id]); if (!q || q <= 0) continue;
      lines.push({ material_id: m.id, parent: groupNameIn(m.group_id, lang), variant: nameIn(m, lang), label_es: `${groupNameIn(m.group_id, 'es')} ${m.name}`.trim(), qty: q, unit: m.group_id ? groupById.get(m.group_id)?.unit : null, unit_cost: Number(m.unit_cost || 0), currency: m.currency || 'ARS', img: imgOf(m) });
    }
    return lines;
  }

  // PDF del pedido a proveedor: es lo que se le MANDA al proveedor, así que va sin
  // precios (son nuestros costos) y con foto de cada insumo para que reconozca la
  // mercadería sin interpretar nombres internos. Diseño:
  //   - Filas de 20mm con foto de 17mm: entran 10 por hoja (11-12 en las siguientes).
  //   - Columna "OK" con casillero para que el proveedor marque lo que separó.
  //   - En el idioma del proveedor (providers.order_lang): textos fijos + name_en.
  //   - Fotos bajadas a 320px (ver src/pdfImages.ts): nítidas a 17mm y PDF liviano.
  // Si una foto falla, la fila sale igual con "sin foto".
  async function genPDF(
    prov: Provider | undefined,
    lines: { parent: string; variant: string; qty: number; unit?: string | null; img?: string | null }[],
    date: Date,
    notes: string,
    lang: 'es' | 'en' = 'es',
    ref?: string | null,
  ) {
    const T = lang === 'en'
      ? { title: 'Purchase order', supplier: 'Supplier', date: 'Date', ref: 'Order', items: 'Items', units: 'Units', photo: 'Photo', product: 'Product', qty: 'Qty', ok: 'OK', notes: 'Notes', noPhoto: 'no photo', page: 'Page', of: 'of', footer: 'Please confirm availability and delivery date. Thank you!', file: 'purchase-order' }
      : { title: 'Pedido de insumos', supplier: 'Proveedor', date: 'Fecha', ref: 'Pedido', items: 'Ítems', units: 'Unidades', photo: 'Foto', product: 'Producto', qty: 'Cant.', ok: 'OK', notes: 'Notas', noPhoto: 'sin foto', page: 'Página', of: 'de', footer: 'Por favor confirmá disponibilidad y fecha de entrega. ¡Gracias!', file: 'pedido' };
    const dateStr = lang === 'en'
      ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })   // "Oct 4, 2026": sin ambigüedad dd/mm
      : date.toLocaleDateString('es-AR');
    const shortRef = ref ? String(ref).replace(/-/g, '').slice(0, 8).toUpperCase() : null;

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const imgs = await loadPdfImages(lines.map(l => l.img), 320);
    const W = 210, M = 14;                       // ancho A4 y margen
    const INK: [number, number, number] = [28, 25, 23];
    const MUTED: [number, number, number] = [120, 113, 108];
    const LINE: [number, number, number] = [231, 229, 228];
    const BRAND: [number, number, number] = [148, 30, 33];

    // ── Encabezado ──
    doc.setFillColor(...BRAND); doc.rect(0, 0, W, 3, 'F');            // filete de marca
    doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(...BRAND);
    doc.text('krilic.', M, 17);
    doc.setFontSize(15); doc.setTextColor(...INK);
    doc.text(T.title, W - M, 15, { align: 'right' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...MUTED);
    doc.text([shortRef ? `${T.ref} #${shortRef}` : '', `${T.date}: ${dateStr}`].filter(Boolean).join('   ·   '), W - M, 21, { align: 'right' });

    // ── Bloque de datos: proveedor a la izquierda, totales a la derecha ──
    const totalUnidades = lines.reduce((a, l) => a + l.qty, 0);
    doc.setDrawColor(...LINE); doc.setFillColor(250, 250, 249);
    doc.roundedRect(M, 27, W - 2 * M, 18, 2, 2, 'FD');
    doc.setFontSize(7.5); doc.setTextColor(...MUTED);
    doc.text(T.supplier.toUpperCase(), M + 4, 33);
    doc.text(T.items.toUpperCase(), W - M - 52, 33);
    doc.text(T.units.toUpperCase(), W - M - 24, 33);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5); doc.setTextColor(...INK);
    doc.text(prov?.name || '—', M + 4, 39.5);
    doc.text(String(lines.length), W - M - 52, 39.5);
    doc.text(String(totalUnidades), W - M - 24, 39.5);
    if (prov?.whatsapp) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...MUTED);
      doc.text(`WhatsApp ${prov.whatsapp}`, M + 4, 43);
    }

    // ── Tabla ──
    const ROW = 20, PHOTO = 17;
    autoTable(doc, {
      startY: 50,
      margin: { left: M, right: M, top: 14, bottom: 16 },
      head: [['#', T.photo, T.product, T.qty, T.ok]],
      body: lines.map((l, i) => [String(i + 1), '', '', '', '']),
      theme: 'plain',
      styles: { font: 'helvetica', fontSize: 9.5, textColor: INK, valign: 'middle', cellPadding: { top: 1.5, bottom: 1.5, left: 2.5, right: 2.5 }, minCellHeight: ROW },
      headStyles: { fontSize: 7.5, fontStyle: 'bold', textColor: MUTED, minCellHeight: 8, fillColor: [245, 245, 244] },
      columnStyles: {
        0: { cellWidth: 9, halign: 'center', textColor: MUTED, fontSize: 8 },
        1: { cellWidth: PHOTO + 5 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 26, halign: 'center' },
        4: { cellWidth: 14, halign: 'center' },
      },
      rowPageBreak: 'avoid',
      // encabezados alineados con su columna (columnStyles no aplica al head)
      didParseCell: (data: any) => {
        if (data.section === 'head' && [0, 3, 4].includes(data.column.index)) data.cell.styles.halign = 'center';
      },
      // línea fina entre filas (theme 'plain' no dibuja bordes)
      didDrawCell: (data: any) => {
        const c = data.cell;
        if (data.section !== 'body') return;
        const l = lines[data.row.index];
        if (data.column.index === 0) {
          doc.setDrawColor(...LINE); doc.setLineWidth(0.2);
          doc.line(M, c.y + c.height, W - M, c.y + c.height);
        }
        if (data.column.index === 1) {
          // marco fijo para que todas las fotos midan lo mismo, con o sin imagen
          const bx = c.x + (c.width - PHOTO) / 2, by = c.y + (c.height - PHOTO) / 2;
          const img = l?.img ? imgs.get(l.img) : null;
          if (img) drawImageInCell(doc, img, { x: bx, y: by, width: PHOTO, height: PHOTO }, 0.5);
          else {
            doc.setFontSize(6.5); doc.setTextColor(...MUTED);
            doc.text(T.noPhoto, bx + PHOTO / 2, by + PHOTO / 2 + 1, { align: 'center' });
          }
          doc.setDrawColor(...LINE); doc.setLineWidth(0.2);
          doc.roundedRect(bx, by, PHOTO, PHOTO, 1.5, 1.5, 'S');
        }
        if (data.column.index === 2 && l) {
          // nombre en negrita + insumo padre debajo, en gris
          const cy = c.y + c.height / 2;
          const nameLines = doc.splitTextToSize(l.variant, c.width - 5).slice(0, 2);
          const sub = l.parent && l.parent !== l.variant ? l.parent : '';
          const blockH = nameLines.length * 4.3 + (sub ? 4 : 0);
          let y = cy - blockH / 2 + 3.2;
          doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...INK);
          doc.text(nameLines, c.x + 2.5, y);
          if (sub) {
            y += nameLines.length * 4.3;
            doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...MUTED);
            doc.text(doc.splitTextToSize(sub, c.width - 5)[0], c.x + 2.5, y);
          }
        }
        if (data.column.index === 3 && l) {
          const cx = c.x + c.width / 2, cy = c.y + c.height / 2;
          doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(...INK);
          doc.text(String(l.qty), cx, cy + (l.unit ? 0.5 : 2), { align: 'center' });
          if (l.unit) {
            doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
            doc.text(l.unit, cx, cy + 5, { align: 'center' });
          }
        }
        if (data.column.index === 4) {
          // casillero para que el proveedor marque lo que separó
          doc.setDrawColor(190, 186, 183); doc.setLineWidth(0.3);
          doc.roundedRect(c.x + c.width / 2 - 2.5, c.y + c.height / 2 - 2.5, 5, 5, 0.8, 0.8, 'S');
        }
      },
    });

    // ── Notas ──
    let y = (doc as any).lastAutoTable.finalY + 8;
    if (notes.trim()) {
      const txt = doc.splitTextToSize(notes.trim(), W - 2 * M - 8);
      const h = 9 + txt.length * 4.5;
      if (y + h > 280) { doc.addPage(); y = 20; }
      doc.setDrawColor(...LINE); doc.setFillColor(250, 250, 249);
      doc.roundedRect(M, y, W - 2 * M, h, 2, 2, 'FD');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
      doc.text(T.notes.toUpperCase(), M + 4, y + 5.5);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...INK);
      doc.text(txt, M + 4, y + 10.5);
    }

    // ── Pie en todas las hojas ──
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setDrawColor(...LINE); doc.setLineWidth(0.2); doc.line(M, 285, W - M, 285);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
      doc.text(`Krilic · ${T.footer}`, M, 290);
      doc.text(`${T.page} ${i} ${T.of} ${pages}`, W - M, 290, { align: 'right' });
    }

    const fileDate = date.toISOString().slice(0, 10);
    doc.save(`${T.file}-${(prov?.name || 'proveedor').replace(/\s+/g, '_')}-${fileDate}.pdf`);
  }

  async function saveOrderAndPdf() {
    if (!ordProvider) { setMsg('Elegí un proveedor'); return; }
    const lines = buildLines();
    if (lines.length === 0) { setMsg('Agregá al menos un insumo con cantidad'); return; }
    setSavingOrder(true);
    try {
      let order: any;
      if (editingOrder) {
        // Editar = mismo pedido (mismo número): se reescriben sus ítems y las notas.
        const { data, error } = await supabase.from('supply_orders')
          .update({ notes: ordNotes.trim() || null, updated_at: new Date().toISOString() })
          .eq('id', editingOrder.id).select().single();
        if (error) throw error;
        const del = await supabase.from('supply_order_items').delete().eq('order_id', editingOrder.id);
        if (del.error) throw del.error;
        order = data;
      } else {
        const { data, error } = await supabase.from('supply_orders').insert({ provider_id: ordProvider, notes: ordNotes.trim() || null, status: 'borrador' }).select().single();
        if (error) throw error;
        order = data;
      }
      // label en español: es el historial interno, aunque el PDF haya salido en inglés
      await supabase.from('supply_order_items').insert(lines.map(l => ({ order_id: order.id, material_id: l.material_id, label: l.label_es, qty: l.qty, unit_cost: l.unit_cost, currency: l.currency })));
      const prov = providers.find(p => p.id === ordProvider);
      await genPDF(prov, lines, new Date(), ordNotes, langOf(ordProvider), order.id);
      const shortId = String(order.id).replace(/-/g, '').slice(0, 8).toUpperCase();
      setOkMsg(`Pedido #${shortId} ${editingOrder ? 'actualizado' : 'guardado'} y PDF descargado (${lines.length} ítem(s)).`);
      setQtys({}); setOrdNotes(''); setOrdProvider(null); setEditingOrder(null); setMsg(null); setOrdSearch(''); setOnlySelected(false); load();
    } catch (e: any) { setMsg('Error: ' + (e.message || e)); }
    setSavingOrder(false);
  }

  async function downloadOrderPdf(order: any) {
    const prov = providers.find(p => p.id === order.provider_id);
    // El ítem guardado tiene material_id: con eso recuperamos la foto actual del insumo
    // (si después le cargaron una, el PDF viejo la muestra igual).
    const lines = (order.supply_order_items || []).map((it: any) => {
      const m = materials.find(x => x.id === it.material_id);
      const lang = langOf(order.provider_id);
      return { parent: m ? groupNameIn(m.group_id, lang) : '', variant: m ? nameIn(m, lang) : (it.label || ''), qty: Number(it.qty) || 0, unit: m?.group_id ? groupById.get(m.group_id)?.unit : null, img: m ? imgOf(m) : null };
    });
    await genPDF(prov, lines, new Date(order.created_at), order.notes || '', langOf(order.provider_id), order.id);
  }
  // Carga un pedido del historial en el armador para corregir cantidades, sumar o sacar ítems.
  function startEditOrder(order: any) {
    const q: Record<number, number> = {};
    let lost = 0;
    for (const it of order.supply_order_items || []) {
      if (it.material_id && materials.some(m => m.id === it.material_id)) q[it.material_id] = Number(it.qty) || 0;
      else lost++;
    }
    setOrdProvider(order.provider_id); setQtys(q); setOrdNotes(order.notes || '');
    setEditingOrder(order); setQuickNew(null); setOkMsg(null); setView('pedidos');
    setMsg(lost ? `${lost} ítem(s) de ese pedido ya no existen como insumo y no se pueden editar.` : null);
  }
  function cancelEditOrder() {
    setEditingOrder(null); setQtys({}); setOrdNotes(''); setOrdProvider(null); setMsg(null);
  }

  async function deleteOrder(order: any) {
    if (!window.confirm('¿Borrar este pedido del historial?')) return;
    await supabase.from('supply_orders').delete().eq('id', order.id);
    setOrders(os => os.filter(o => o.id !== order.id));
    if (editingOrder?.id === order.id) cancelEditOrder();
  }

  const orderTotalCount = useMemo(() => Object.values(qtys).reduce((s, q) => s + (Number(q) > 0 ? Number(q) : 0), 0), [qtys]);
  const orderItemCount = useMemo(() => Object.values(qtys).filter(q => Number(q) > 0).length, [qtys]);
  // Estimado SOLO de lo que está en pesos: mezclar USD daría un número sin sentido.
  // Es orientativo (el precio final lo pone el proveedor), por eso dice "estimado".
  const orderTotalArs = useMemo(() => materials.reduce((acc, m) => {
    const q = Number(qtys[m.id]) || 0;
    if (q <= 0 || (m.currency || 'ARS') !== 'ARS') return acc;
    return acc + q * Number(m.unit_cost || 0);
  }, 0), [qtys, materials]);

  const setQty = (id: number, q: number) => setQtys(x => {
    const n = { ...x };
    if (q > 0) n[id] = q; else delete n[id];   // 0 = fuera del pedido
    return n;
  });
  const removeFromOrder = (id: number) => setQty(id, 0);
  function clearOrder() {
    if (orderItemCount > 0 && !window.confirm(`¿Sacar los ${orderItemCount} productos del pedido?`)) return;
    setQtys({}); setOnlySelected(false);
  }
  // Cambiar de proveedor borra lo cargado (cada pedido es de UN proveedor): se confirma.
  function changeOrderProvider(v: number | null) {
    if (orderItemCount > 0 && v !== ordProvider
      && !window.confirm(`Tenés ${orderItemCount} producto(s) cargados para ${providerName(ordProvider)}. Si cambiás de proveedor se sacan del pedido. ¿Seguir?`)) return;
    setOrdProvider(v); setQtys({}); setQuickNew(null); setOrdSearch(''); setOnlySelected(false); setOkMsg(null);
  }
  // Lo que se ve en el armado: búsqueda (nombre, nombre en inglés, insumo padre) + "solo los del pedido".
  const visibleProviderItems = (() => {
    const t = ordSearch.trim().toLowerCase();
    return providerItems
      .map(g => ({ ...g, items: g.items.filter(m =>
        (!onlySelected || (Number(qtys[m.id]) || 0) > 0)
        && (!t || `${m.name} ${m.name_en || ''} ${g.parent}`.toLowerCase().includes(t))) }))
      .filter(g => g.items.length > 0);
  })();
  // Líneas del pedido en curso, para el resumen (mismo orden que las tarjetas).
  const orderLinesUI = providerItems.flatMap(g => g.items).filter(m => (Number(qtys[m.id]) || 0) > 0);
  const ungroupedCount = materials.filter(m => !m.group_id).length;
  const providerOptions = providers.map(p => ({ value: p.id, label: p.name, sublabel: `${countForProvider(p.id)} insumo(s)` }));

  return (
    <div className="tw w-full min-w-0 pb-12 pt-1">
      {/* ── Encabezado ─────────────────────────────────────────────────────── */}
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Insumos</h1>
          <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
            {view === 'insumos'
              ? 'Precios, fotos y proveedor de cada variante. Lo que cambies en la tabla se guarda solo.'
              : 'Elegí el proveedor, cargá cantidades y descargá el PDF para mandarle: sale con la foto grande de cada insumo y sin precios.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl label="Vista" value={view} onValueChange={v => { setView(v as 'insumos' | 'pedidos'); setMsg(null); }}
            options={[
              { value: 'insumos', label: 'Insumos', accessory: <Badge size="sm">{materials.length}</Badge> },
              { value: 'pedidos', label: 'Pedidos', accessory: orders.length ? <Badge size="sm">{orders.length}</Badge> : undefined },
            ]} />
          {view === 'insumos' && <>
            {/* Atajo al pedido: si hay un proveedor filtrado, entra con ese proveedor elegido. */}
            <Button variant="secondary" onClick={() => { setView('pedidos'); setMsg(null); if (fProv != null) { setOrdProvider(fProv); setQtys({}); } }}>
              <ShoppingCart size={16} /> Hacer pedido
            </Button>
            <Button onClick={newGroup}><Plus size={16} /> Nuevo insumo</Button>
          </>}
        </div>
      </header>

      {/* Error de la última acción. Escrito a mano: el `alert` de uiarc es para avisos
          persistentes con título; esto es una línea que se descarta. */}
      {okMsg && (
        <div role="status" className="mb-4 flex items-center gap-2 rounded-[var(--radius-control)] border border-[color-mix(in_oklab,var(--success)_30%,var(--border))] bg-[color-mix(in_oklab,var(--success)_8%,var(--card))] px-3 py-2 text-[length:var(--text-sm)] text-[var(--success)]">
          <CheckCircle2 size={16} className="shrink-0" />
          <span className="min-w-0 flex-1">{okMsg}</span>
          <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={() => setOkMsg(null)}><X size={14} /></Button>
        </div>
      )}
      {msg && (
        <div role="alert" className="mb-4 flex items-center gap-2 rounded-[var(--radius-control)] border border-[color-mix(in_oklab,var(--danger)_30%,var(--border))] bg-[color-mix(in_oklab,var(--danger)_8%,var(--card))] px-3 py-2 text-[length:var(--text-sm)] text-[var(--danger)]">
          <AlertCircle size={16} className="shrink-0" />
          <span className="min-w-0 flex-1">{msg}</span>
          <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={() => setMsg(null)}><X size={14} /></Button>
        </div>
      )}

      {view === 'insumos' && (<>
        {/* ── Resumen ─────────────────────────────────────────────────────── */}
        <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <MetricCard label="Variantes" value={materials.length} locale="es-AR"
            context={`${groups.length} insumos padre · ${ungroupedCount} sin agrupar`} />
          <MetricCard label="Sin precio" value={counts.sin_precio} locale="es-AR"
            context="Costean en $0 los productos que los usan" />
          <MetricCard label="Sin foto" value={counts.sin_foto} locale="es-AR"
            context="Salen sin miniatura en el PDF del pedido" />
          <MetricCard label="Sin proveedor" value={counts.sin_proveedor} locale="es-AR"
            context="No aparecen al armar un pedido" />
        </section>

        {/* ── Panel del insumo padre (alta / edición) ─────────────────────── */}
        {groupForm && (
          <section className="mb-5 rounded-[var(--radius-surface)] border border-line bg-card p-5 shadow-xs animate-in fade-in-0 slide-in-from-top-2">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-[length:var(--text-base)] font-semibold text-ink">{groupForm.id ? 'Editar insumo' : 'Nuevo insumo'}</h2>
                <p className="mt-0.5 text-[length:var(--text-sm)] text-faint">
                  {groupForm.id ? 'Nombre, proveedor, unidad y foto del insumo padre. Las variantes se editan en la tabla.'
                    : 'Se crea con una primera variante del mismo nombre; después la renombrás o agregás más.'}
                </p>
              </div>
              <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={closeGroup}><X size={16} /></Button>
            </div>

            <div className="flex flex-col gap-5 sm:flex-row">
              <div className="flex flex-col items-start gap-2">
                <span className="text-[length:var(--text-sm)] font-medium text-ink">Foto</span>
                <ImageDrop size="lg"
                  src={groupImgUrl || groupForm.image_url}
                  alt={groupForm.name || ''}
                  onFile={setGroupImg}
                  onClear={(groupImg || groupForm.image_url) ? () => { setGroupImg(null); setGroupForm(f => f ? { ...f, image_url: null } : f); } : undefined} />
              </div>
              <div className="grid min-w-0 flex-1 gap-4 sm:grid-cols-2">
                <Input label="Nombre" placeholder="ej: Acrílico 4mm" value={groupForm.name || ''}
                  onChange={e => setGroupForm(f => ({ ...f!, name: e.target.value }))} />
                <div className="grid min-w-0">
                  <span className="mb-2 text-[length:var(--text-sm)] font-medium text-ink">Proveedor</span>
                  {!newProvOpen ? (
                    <div className="flex gap-2">
                      <PickerSelect className="min-w-0 flex-1" placeholder="Sin proveedor" clearable
                        value={groupForm.provider_id ?? null}
                        onChange={v => setGroupForm(f => ({ ...f!, provider_id: v == null ? null : Number(v) }))}
                        options={providerOptions} />
                      <Button variant="secondary" type="button" aria-label="Nuevo proveedor" title="Nuevo proveedor" onClick={() => setNewProvOpen(true)}><Plus size={16} /></Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input autoFocus placeholder="Nombre del proveedor" value={newProvName}
                        onChange={e => setNewProvName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addProviderInline()}
                        className="h-[var(--control-height-md)] min-w-0 flex-1 rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-3 text-[length:var(--text-sm)] text-ink focus:border-[var(--foreground)] focus:outline-none" />
                      <Button type="button" onClick={addProviderInline}>Crear</Button>
                      <Button variant="ghost" type="button" aria-label="Cancelar" onClick={() => { setNewProvOpen(false); setNewProvName(''); }}><X size={16} /></Button>
                    </div>
                  )}
                </div>
                <Input label="Nombre en inglés" placeholder="Para pedidos en inglés (opcional)" value={groupForm.name_en || ''}
                  onChange={e => setGroupForm(f => ({ ...f!, name_en: e.target.value }))} />
                <Input label="Unidad" placeholder="placa, kg, unidad" value={groupForm.unit || ''}
                  onChange={e => setGroupForm(f => ({ ...f!, unit: e.target.value }))} />
                <Input label="Notas" placeholder="Opcional" value={groupForm.notes || ''}
                  onChange={e => setGroupForm(f => ({ ...f!, notes: e.target.value }))} />
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-4">
              <div className="flex flex-wrap gap-2">
                {groupForm.id && <>
                  <Button variant="secondary" size="sm" onClick={() => addVariant(groupForm.id!)}><Plus size={14} /> Agregar variante</Button>
                  <Button variant="danger" size="sm" onClick={() => deleteGroup(groupForm)}><Trash2 size={14} /> Borrar insumo</Button>
                </>}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={closeGroup}>Cancelar</Button>
                <Button loading={savingGroup} onClick={saveGroup}>{groupForm.id ? 'Guardar cambios' : 'Crear insumo'}</Button>
              </div>
            </div>
          </section>
        )}

        {/* ── Grilla de insumos ───────────────────────────────────────────────
            Grilla PLANA: una fila por variante, con el insumo padre como columna.
            Todo se edita acá mismo y se autoguarda (useRowAutosave). */}
        <DataGrid
          rows={gridRows}
          loading={loading}
          rowKey={(r) => r.m.id}
          pageSize={50}
          searchPlaceholder="Buscar insumo, variante o proveedor…"
          emptyTitle="Todavía no hay insumos"
          emptyText="Creá el primero y cargale variantes con su precio."
          emptyAction={<Button size="sm" onClick={newGroup}><Plus size={14} /> Nuevo insumo</Button>}
          filtersActive={fProv != null || fFlag != null}
          onClearFilters={() => { setFProv(null); setFFlag(null); }}
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <PickerSelect className="w-56" placeholder="Todos los proveedores" clearable
                value={fProv} onChange={v => setFProv(v == null ? null : Number(v))}
                options={providerOptions} />
              <ChipGroup label="Filtros rápidos" multiple={false}
                value={fFlag ? [fFlag] : []}
                onValueChange={v => setFFlag((v[0] as Flag) ?? null)}
                options={[
                  { value: 'sin_precio', label: `Sin precio · ${counts.sin_precio}` },
                  { value: 'sin_foto', label: `Sin foto · ${counts.sin_foto}` },
                  { value: 'sin_proveedor', label: `Sin proveedor · ${counts.sin_proveedor}` },
                  { value: 'usd', label: `En USD · ${counts.usd}` },
                ]} />
            </div>
          }
          cols={[
            { key: 'foto', header: '', width: 'w-16',
              render: ({ m }) => (
                <ImageDrop src={imgOf(m)} alt={m.name} size="sm" busy={imgBusy === m.id}
                  hint="Arrastrá la foto, hacé click o pegá del portapapeles"
                  onFile={f => uploadVariantImage(m, f)}
                  onClear={m.image_url ? () => editNow(m.id, { image_url: null }) : undefined} />
              ) },
            { key: 'variante', header: 'Variante', sort: ({ m }) => m.name,
              render: ({ m }) => <TextCell value={m.name} placeholder="Nombre" className="min-w-[10rem] font-medium"
                autoFocus={renameId === m.id} onBlur={renameId === m.id ? () => setRenameId(null) : undefined}
                onChange={v => edit(m.id, { name: v })} /> },
            { key: 'name_en', header: 'Nombre en inglés', hideOnMobile: true, sort: ({ m }) => m.name_en || '',
              // Para proveedores con pedido en inglés (ej. Christopher, Alibaba). Vacío = el PDF usa el español.
              render: ({ m }) => <TextCell value={m.name_en} placeholder="Agregar" className="min-w-[10rem] text-soft" onChange={v => edit(m.id, { name_en: v || null })} /> },
            // Con 0 insumos padre la columna sería 75 filas de "Sin agrupar": se muestra recién
            // cuando hay grupos a los que asignar.
            ...(groups.length === 0 ? [] : [{ key: 'grupo', header: 'Insumo padre', hideOnMobile: true, sort: ({ g }: GridRow) => g?.name || '',
              render: ({ m, g }: GridRow) => g ? (
                <button type="button" onClick={() => editGroup(g)} title="Editar el insumo padre (nombre, proveedor, unidad, foto)"
                  className="group/g inline-flex max-w-[14rem] items-center gap-1.5 rounded-[var(--radius-control)] px-2 py-1 text-[length:var(--text-sm)] text-soft transition-colors hover:bg-muted hover:text-ink">
                  <span className="truncate">{g.name}</span>
                  <Pencil size={12} className="shrink-0 opacity-0 transition-opacity group-hover/g:opacity-100" />
                </button>
              ) : (
                <SelectCell value="" className="max-w-[12rem] text-faint" title="Asignar a un insumo padre"
                  onChange={v => v && assignToGroup(m.id, v)}
                  options={[{ value: '', label: 'Sin agrupar — asignar…' }, ...groups.map(x => ({ value: x.id, label: x.name }))]} />
              ) } as GridCol<GridRow>]),
            { key: 'tipo', header: 'Costeo', width: 'w-40', hideOnMobile: true, sort: ({ m }) => m.type || 'simple',
              render: ({ m }) => (
                <SelectCell value={m.type || 'simple'} onChange={v => editNow(m.id, { type: v })}
                  title="Por unidad, o acrílico (se cobra por kg usando las medidas de la pieza)"
                  options={[{ value: 'simple', label: 'Por unidad' }, { value: 'acrylic', label: 'Acrílico (kg)' }]} />
              ) },
            { key: 'costo', header: 'Costo', align: 'right', width: 'w-44', sort: ({ m }) => Number(m.unit_cost || 0),
              render: ({ m }) => (
                <div className="flex items-center justify-end gap-1">
                  <SelectCell value={m.currency || 'ARS'} className="w-[4.75rem] shrink-0 text-faint" title="Moneda"
                    onChange={v => editNow(m.id, { currency: v })}
                    options={[{ value: 'ARS', label: 'ARS' }, { value: 'USD', label: 'USD' }]} />
                  <NumberCell value={m.unit_cost} placeholder="0" className={cn('w-24', !(Number(m.unit_cost) > 0) && 'text-[var(--warning)]')}
                    onChange={v => edit(m.id, { unit_cost: v })} />
                </div>
              ) },
            { key: 'proveedor', header: 'Proveedor', hideOnMobile: true, sort: ({ m }) => providerName(provOf(m)) || '',
              render: ({ m, g }) => g ? (
                // Agrupada: el proveedor es el del insumo padre (se cambia desde su panel).
                <span className="block max-w-[12rem] truncate px-2 text-[length:var(--text-sm)] text-soft" title="Proveedor del insumo padre">
                  {providerName(g.provider_id) || <span className="text-faint">—</span>}
                </span>
              ) : (
                <SelectCell value={m.provider_id == null ? '' : String(m.provider_id)} className="max-w-[12rem]"
                  onChange={v => editNow(m.id, { provider_id: v ? Number(v) : null })}
                  options={[{ value: '', label: '—' }, ...providers.map(p => ({ value: String(p.id), label: p.name }))]} />
              ) },
            { key: 'acciones', header: '', width: 'w-40', align: 'right',
              render: ({ m }) => (
                <div className="flex items-center justify-end gap-1">
                  <SaveState state={state[String(m.id)]} />
                  <Button variant="ghost" size="sm" aria-label={`Duplicar ${m.name}`} title="Duplicar (misma foto, precio y proveedor; cambiás el nombre)"
                    className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                    onClick={() => duplicateMaterial(m)}>
                    <Copy size={14} />
                  </Button>
                  {/* El borrado sí confirma: es irreversible y puede estar en el costeo de un producto. */}
                  <Button variant="ghost" size="sm" aria-label={`Borrar ${m.name}`} title="Borrar variante"
                    className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                    onClick={() => deleteVariant(m)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ) },
          ]}
        />
      </>)}

      {view === 'pedidos' && (
        <div className="grid items-start gap-4 lg:grid-cols-[1.6fr_1fr]">
          {/* ── Armado del pedido ─────────────────────────────────────────── */}
          <section className={cn('min-w-0 overflow-hidden rounded-[var(--radius-surface)] border bg-card shadow-xs',
            editingOrder ? 'border-[var(--foreground)]' : 'border-line')}>
            {editingOrder && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-muted px-4 py-2.5">
                <span className="flex items-center gap-2 text-[length:var(--text-sm)] text-ink">
                  <Pencil size={14} />
                  Editando pedido <b className="font-semibold">#{String(editingOrder.id).replace(/-/g, '').slice(0, 8).toUpperCase()}</b>
                  <span className="text-faint">del {new Date(editingOrder.created_at).toLocaleDateString('es-AR')}</span>
                </span>
                <Button variant="ghost" size="sm" onClick={cancelEditOrder}><X size={14} /> Cancelar edición</Button>
              </div>
            )}
            <div className="flex flex-wrap items-end gap-3 border-b border-line p-4">
              <div className="min-w-[14rem] flex-1">
                <span className="mb-2 block text-[length:var(--text-sm)] font-medium text-ink">Proveedor</span>
                <PickerSelect placeholder="Elegí un proveedor…" clearable disabled={!!editingOrder}
                  value={ordProvider ?? null}
                  onChange={v => changeOrderProvider(v == null ? null : Number(v))}
                  options={providerOptions} />
              </div>
              {ordProvider != null && (
                <div className="flex flex-col gap-1">
                  <span className="text-[length:var(--text-sm)] font-medium text-ink">Idioma del PDF</span>
                  {/* Se guarda en el proveedor: la próxima vez ya sale en su idioma. */}
                  <SegmentedControl label="Idioma del PDF" value={langOf(ordProvider)}
                    onValueChange={v => setProviderLang(ordProvider, v as 'es' | 'en')}
                    options={[{ value: 'es', label: 'Español' }, { value: 'en', label: 'English' }]} />
                </div>
              )}
            </div>

            {ordProvider == null ? (
              <EmptyState icon={<Store width={22} height={22} strokeWidth={1.5} />}
                title="Elegí un proveedor" description="Vas a ver sus insumos con foto y precio para cargar cantidades." />
            ) : (
              <>
                {/* Alta rápida: el insumo que falta se crea acá, sin salir del pedido. */}
                {quickNew ? (
                  <div className="flex flex-wrap items-end gap-3 border-b border-line bg-muted p-4 animate-in fade-in-0">
                    <ImageDrop size="md" src={quickImgUrl}
                      hint="Foto del insumo: arrastrá, hacé click o pegá"
                      onFile={f => setQuickNew(q => q && { ...q, file: f })}
                      onClear={quickNew.file ? () => setQuickNew(q => q && { ...q, file: null }) : undefined} />
                    <div className="grid min-w-[12rem] flex-1 gap-3 sm:grid-cols-2">
                      <Input label="Nombre del insumo" placeholder="ej: Tornillo M4 10mm" autoFocus value={quickNew.name}
                        onChange={e => setQuickNew(q => q && { ...q, name: e.target.value })}
                        onKeyDown={e => e.key === 'Enter' && createQuickMaterial()} />
                      <Input label="Nombre en inglés" description={langOf(ordProvider) === 'en' ? 'Es lo que lee el proveedor en el PDF' : 'Opcional'}
                        placeholder="ej: Screw M4 10mm" value={quickNew.name_en}
                        onChange={e => setQuickNew(q => q && { ...q, name_en: e.target.value })}
                        onKeyDown={e => e.key === 'Enter' && createQuickMaterial()} />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="secondary" onClick={() => setQuickNew(null)}>Cancelar</Button>
                      <Button loading={creatingQuick} disabled={!quickNew.name.trim()} onClick={createQuickMaterial}>Crear y agregar</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
                    <span className="text-[length:var(--text-xs)] text-faint">¿Falta algo? Crealo acá: queda asignado a {providerName(ordProvider)}.</span>
                    <Button variant="secondary" size="sm" onClick={() => setQuickNew({ name: '', name_en: '', file: null })}><Plus size={14} /> Nuevo insumo</Button>
                  </div>
                )}
                {providerItems.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
                    <div className="min-w-[12rem] flex-1">
                      <SearchField label="Buscar insumo" hideLabel value={ordSearch} onValueChange={setOrdSearch}
                        placeholder={`Buscar entre los ${providerItems.reduce((a, g) => a + g.items.length, 0)} insumos…`} />
                    </div>
                    <Button variant={onlySelected ? 'primary' : 'secondary'} size="sm" disabled={orderItemCount === 0 && !onlySelected}
                      onClick={() => setOnlySelected(v => !v)}>
                      Solo los del pedido · {orderItemCount}
                    </Button>
                  </div>
                )}
                {providerItems.length === 0 ? (
                  <EmptyState icon={<PackageOpen width={22} height={22} strokeWidth={1.5} />}
                    title="Este proveedor no tiene insumos"
                    description="Crealo con “Nuevo insumo” o asignale el proveedor a los que ya existen desde la vista Insumos." />
                ) : visibleProviderItems.length === 0 ? (
                  <EmptyState title="Nada coincide"
                    description={onlySelected ? 'El pedido está vacío.' : `No hay insumos que coincidan con “${ordSearch.trim()}”.`}
                    action={<Button variant="secondary" size="sm" onClick={() => { setOrdSearch(''); setOnlySelected(false); }}>Ver todos</Button>} />
                ) : (
                <div className="max-h-[60vh] overflow-y-auto p-4">
                  {visibleProviderItems.map(g => (
                    <div key={g.key} className="mb-4 last:mb-0">
                      <div className="mb-2 text-[length:var(--text-xs)] font-medium uppercase tracking-wide text-faint">{g.parent}</div>
                      <div className="grid gap-2 sm:grid-cols-2 2xl:grid-cols-3">
                        {g.items.map(m => {
                          const q = Number(qtys[m.id]) || 0;
                          return (
                            <div key={m.id}
                              className={cn('group/card flex items-center gap-3 rounded-[var(--radius-control)] border p-2 transition-colors',
                                q > 0 ? 'border-[var(--foreground)] bg-muted' : 'border-line')}>
                              <Thumb src={imgOf(m)} name={m.name} size="lg" />
                              <div className="min-w-0 flex-1">
                                {renameId === m.id
                                  ? <TextCell value={m.name} autoFocus className="-ml-2 font-medium" onBlur={() => setRenameId(null)} onChange={v => edit(m.id, { name: v })} />
                                  : <div className="flex items-center gap-1">
                                      {/* 2 líneas en vez de cortar: con la ✕ y el stepper queda poco ancho */}
                                      <span className="line-clamp-2 break-words text-[length:var(--text-sm)] font-medium leading-snug text-ink" title={m.name}>{m.name}</span>
                                      <button type="button" title="Duplicar (misma foto y precio; cambiás el nombre)" aria-label={`Duplicar ${m.name}`}
                                        onClick={() => duplicateMaterial(m)}
                                        className="shrink-0 rounded p-0.5 text-faint opacity-0 transition-opacity hover:text-ink focus-visible:opacity-100 group-hover/card:opacity-100">
                                        <Copy size={12} />
                                      </button>
                                    </div>}
                                {langOf(ordProvider) === 'en' && (
                                  // Lo que va a leer el proveedor. Si falta, el PDF usa el español: se avisa.
                                  m.name_en?.trim()
                                    ? <div className="truncate text-[length:var(--text-xs)] text-soft" title={m.name_en}>EN: {m.name_en}</div>
                                    : <div className="text-[length:var(--text-xs)] text-[var(--warning)]">Sin nombre en inglés</div>
                                )}
                                <div className="text-[length:var(--text-xs)] tabular-nums text-faint">
                                  {/* el costo es referencia interna: NO sale en el PDF del proveedor */}
                                  {m.currency || 'ARS'} {Number(m.unit_cost || 0).toLocaleString('es-AR')}
                                  {!imgOf(m) && <span className="ml-1.5 text-[var(--warning)]">· sin foto</span>}
                                </div>
                              </div>
                              {/* Stepper: en un pedido se suma de a uno, y tipear igual se puede */}
                              <div className="flex shrink-0 items-center gap-1">
                                {q > 0 && (
                                  <Button variant="ghost" size="sm" className="w-8 px-0" aria-label={`Quitar ${m.name} del pedido`} title="Quitar del pedido"
                                    onClick={() => removeFromOrder(m.id)}><X size={14} /></Button>
                                )}
                                <Button variant="secondary" size="sm" className="w-8 px-0" aria-label="Quitar uno" disabled={q === 0}
                                  onClick={() => setQty(m.id, q - 1)}>−</Button>
                                <input type="number" min={0} value={qtys[m.id] ?? ''} placeholder="0" aria-label={`Cantidad de ${m.name}`}
                                  onChange={e => setQty(m.id, Number(e.target.value) || 0)}
                                  className="h-8 w-12 rounded-[var(--radius-control)] border border-line bg-card text-center text-[length:var(--text-sm)] tabular-nums text-ink focus:border-[var(--foreground)] focus:outline-none" />
                                <Button variant="secondary" size="sm" className="w-8 px-0" aria-label="Agregar uno"
                                  onClick={() => setQty(m.id, q + 1)}>+</Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                )}

                <div className="border-t border-line p-4">
                  {/* Resumen: qué lleva el pedido, con quitar de a uno y vaciar. */}
                  {orderLinesUI.length > 0 && (
                    <div className="mb-4">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-[length:var(--text-sm)] font-medium text-ink">En este pedido · {orderLinesUI.length}</span>
                        <Button variant="ghost" size="sm" onClick={clearOrder}><Eraser size={14} /> Vaciar</Button>
                      </div>
                      <ul className="m-0 max-h-56 list-none overflow-y-auto rounded-[var(--radius-control)] border border-line p-0">
                        {orderLinesUI.map(m => (
                          <li key={m.id} className="flex items-center gap-2 border-b border-[var(--border-subtle)] px-2 py-1.5 last:border-0">
                            <Thumb src={imgOf(m)} name={m.name} size="sm" />
                            <span className="min-w-0 flex-1 truncate text-[length:var(--text-sm)] text-ink" title={m.name}>{m.name}</span>
                            <span className="shrink-0 text-[length:var(--text-sm)] font-semibold tabular-nums text-ink">× {qtys[m.id]}</span>
                            <Button variant="ghost" size="sm" className="w-8 px-0" aria-label={`Quitar ${m.name} del pedido`} title="Quitar del pedido"
                              onClick={() => removeFromOrder(m.id)}><X size={14} /></Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Input label="Notas (opcional)" placeholder="ej: entregar antes del viernes"
                    value={ordNotes} onChange={e => setOrdNotes(e.target.value)} />
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="text-[length:var(--text-sm)] tabular-nums text-soft">
                      <b className="font-semibold text-ink">{orderItemCount}</b> ítem(s) · <b className="font-semibold text-ink">{orderTotalCount}</b> unidad(es)
                      {orderTotalArs > 0 && <> · estimado <b className="font-semibold text-ink">${Math.round(orderTotalArs).toLocaleString('es-AR')}</b></>}
                    </div>
                    <Button loading={savingOrder} disabled={orderTotalCount === 0} onClick={saveOrderAndPdf}>
                      <FileDown size={16} /> {editingOrder ? 'Guardar cambios y descargar PDF' : 'Descargar PDF del pedido'}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* ── Historial ─────────────────────────────────────────────────── */}
          <section className="min-w-0 overflow-hidden rounded-[var(--radius-surface)] border border-line bg-card shadow-xs">
            <div className="flex items-center gap-2 border-b border-line px-4 py-3 text-[length:var(--text-sm)] font-semibold text-ink">
              <ClipboardList size={16} /> Pedidos anteriores
              {orders.length > 0 && <Badge size="sm" className="ml-auto">{orders.length}</Badge>}
            </div>
            {orders.length === 0 ? (
              <EmptyState icon={<ClipboardList width={22} height={22} strokeWidth={1.5} />}
                title="Sin pedidos todavía" description="Los pedidos que generes quedan acá para volver a bajar el PDF." />
            ) : (
              <ul className="m-0 max-h-[70vh] list-none overflow-y-auto p-0">
                {orders.map(o => (
                  <li key={o.id} className={cn('group flex items-center justify-between gap-2 border-b border-[var(--border-subtle)] px-4 py-2.5 last:border-0 hover:bg-[color-mix(in_oklab,var(--bg-secondary)_55%,transparent)]',
                    editingOrder?.id === o.id && 'bg-muted')}>
                    <div className="min-w-0">
                      <div className="truncate text-[length:var(--text-sm)] font-medium text-ink">{providerName(o.provider_id) || '—'}</div>
                      <div className="text-[length:var(--text-xs)] tabular-nums text-faint">
                        #{String(o.id).replace(/-/g, '').slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleDateString('es-AR')} · {(o.supply_order_items || []).length} ítem(s)
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button variant="ghost" size="sm" title="Editar el pedido (cantidades, ítems, notas)" aria-label="Editar pedido" onClick={() => startEditOrder(o)}><Pencil size={15} /></Button>
                      <Button variant="ghost" size="sm" title="Descargar PDF de nuevo" aria-label="Descargar PDF" onClick={() => downloadOrderPdf(o)}><FileDown size={15} /></Button>
                      <Button variant="ghost" size="sm" title="Borrar del historial" aria-label="Borrar pedido"
                        className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                        onClick={() => deleteOrder(o)}><Trash2 size={15} /></Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default Insumos;
